import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Avatar,
  Button,
  Alert,
  Skeleton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
} from "@mui/material";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import LockIcon from "@mui/icons-material/Lock";
import Swal from "sweetalert2";
import { api } from "../../utils/api";

const emptyForm = { id: 0, name: "", description: "", sort_order: 100 };

export default function Roles() {
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [msg, setMsg] = useState(null);

  const [dialog, setDialog] = useState({ open: false, form: emptyForm });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/roles/list.php");
      setRoles(res.roles || []);
    } catch (e) {
      setMsg({ type: "error", text: e.message || "Failed to load roles" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => setDialog({ open: true, form: emptyForm });
  const openEdit = (r) =>
    setDialog({
      open: true,
      form: {
        id: r.id,
        name: r.name,
        description: r.description || "",
        sort_order: r.sort_order || 100,
      },
    });
  const closeDialog = () => setDialog({ open: false, form: emptyForm });

  const setField = (k, v) =>
    setDialog((p) => ({ ...p, form: { ...p.form, [k]: v } }));

  const save = async () => {
    const { name } = dialog.form;
    if (!name.trim()) {
      Swal.fire({ icon: "warning", title: "Name required" });
      return;
    }
    setSaving(true);
    try {
      await api.post("/roles/save.php", dialog.form);
      closeDialog();
      load();
      Swal.fire({
        icon: "success",
        title: "Saved",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (e) {
      Swal.fire({ icon: "error", title: "Save Failed", text: e.message });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r) => {
    const c = await Swal.fire({
      icon: "warning",
      title: `Delete role "${r.name}"?`,
      text: "This removes the role and its permission grants. Users with this role must be reassigned first.",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#d33",
    });
    if (!c.isConfirmed) return;

    try {
      await api.post("/roles/delete.php", { id: r.id });
      load();
      Swal.fire({
        icon: "success",
        title: "Deleted",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (e) {
      Swal.fire({ icon: "error", title: "Delete Failed", text: e.message });
    }
  };

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar sx={{ bgcolor: "primary.main", width: 48, height: 48 }}>
            <GroupWorkIcon />
          </Avatar>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              Roles
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create and manage roles. Each role can be granted permissions.
            </Typography>
          </Box>
        </Stack>

        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Add Role
        </Button>
      </Stack>

      {msg && (
        <Alert severity={msg.type} sx={{ mb: 2 }} onClose={() => setMsg(null)}>
          {msg.text}
        </Alert>
      )}

      <Paper>
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Users</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading &&
                [...Array(5)].map((_, i) => (
                  <TableRow key={`sk-${i}`}>
                    <TableCell colSpan={6}>
                      <Skeleton height={36} />
                    </TableCell>
                  </TableRow>
                ))}

              {!loading && roles.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No roles found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {!loading &&
                roles.map((r, idx) => (
                  <TableRow key={r.id} hover>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {r.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {r.description || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={r.user_count}
                        color={r.user_count > 0 ? "info" : "default"}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {r.is_system == 1 ? (
                        <Chip
                          size="small"
                          label="System"
                          color="primary"
                          icon={<LockIcon sx={{ fontSize: 14 }} />}
                        />
                      ) : (
                        <Chip size="small" label="Custom" color="secondary" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={r.is_system == 1 ? "Edit description / order only" : "Edit"}>
                        <IconButton size="small" color="primary" onClick={() => openEdit(r)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={r.is_system == 1 ? "System roles cannot be deleted" : "Delete"}>
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={r.is_system == 1}
                            onClick={() => remove(r)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      {/* Dialog */}
      <Dialog open={dialog.open} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{dialog.form.id ? "Edit Role" : "Add Role"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Name *"
              fullWidth
              value={dialog.form.name}
              onChange={(e) => setField("name", e.target.value)}
              helperText="Start with a letter. Letters, digits, spaces, _ or - allowed."
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={2}
              value={dialog.form.description}
              onChange={(e) => setField("description", e.target.value)}
            />
            <TextField
              label="Sort Order"
              type="number"
              fullWidth
              value={dialog.form.sort_order}
              onChange={(e) => setField("sort_order", parseInt(e.target.value, 10) || 0)}
              helperText="Lower numbers show first"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
